import { ReactElement, useState } from "react";
import { Calendar} from "../../../store";
import { Alert, Box, Divider, FormControl, FormLabel, Skeleton, Typography, styled } from "@mui/material";
import { CabButton } from "@CabComponents/CabButton";
import { CabIcon } from "@CabComponents/CabIcon";
import colors from "../../../colors";
import { Controller, useForm } from "react-hook-form";
import { CabModal } from "@CabComponents/CabModal";
import { CabTextInput } from "@CabComponents/CabTextInput";
import { EVENT_TYPE, emailRegex } from "../../../constants";
import { trackEvent } from "../../../utils/appAnalyticsUtils";
import { CabToggleChip } from "@CabComponents/CabToggleChip";
import CabSpinner from "@CabComponents/CabSpinner";
import { IoBulbOutline, IoCheckmarkCircleOutline } from "react-icons/io5";


export interface MissingCalendarsProps {
  handleSetAdditionalCalendars: (value: { [eml: string]: Calendar[] }) => void
  additionalCalendars: { [eml: string]: Calendar[] }
  onGetEmailCalendars: (email: string) => Promise<Calendar[]>
  additionalDelegateEmails: (string | null)[];
  loading: boolean;
  variant?: 'card' | 'inlineLink';
}

export const MissingCalendars = ({
  handleSetAdditionalCalendars, additionalCalendars, onGetEmailCalendars, additionalDelegateEmails, loading,
  variant = 'card',
}: MissingCalendarsProps): ReactElement => {

  const [showEmailModal, setShowEmailModal] = useState(false);

  const additionalEmails = [...new Set([...Object.keys(additionalCalendars), ...additionalDelegateEmails])];

  const {
    control: emailControl, formState: { errors: emailErrors, isSubmitting }, setError, handleSubmit: handleEmailSubmit,
    getValues, reset
  } = useForm<{ email: string }>({
    defaultValues: { email: '' },
  });

  const handleGetEmailCalendars = async (email: string) => {
    const calendars = await onGetEmailCalendars(email);
    if (calendars.length === 0) {
      setError('email', { message: 'No calendars found. Please try another email address.' });
    } else {
      trackEvent(EVENT_TYPE.ONBOARD_MS_DELEGATE);
      handleSetAdditionalCalendars({ ...additionalCalendars, [email]: calendars });
      setShowEmailModal(false);
      reset();
    }
  };

  const emailModal = (
    <CabModal
      open={showEmailModal}
      onClose={() => setShowEmailModal(false)}
      title="Fetch calendars from email"
      closeOnBackdropClick={!isSubmitting}
      closeIcon={!isSubmitting}
      actionButtons={<>
        <CabButton
          onClick={() => setShowEmailModal(false)}
          buttonType="secondary"
          disabled={isSubmitting}
        >Cancel</CabButton>
        <CabButton
          onClick={() => handleEmailSubmit(({ email }) => handleGetEmailCalendars(email))()}
          disabled={isSubmitting}
        >Fetch Calendars</CabButton>
      </>}
    >
      {isSubmitting ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}>
          <Box sx={{
            margin: 2
          }}>
            <CabSpinner scale={1.0} />
          </Box>
          <Typography color={colors.black600}>Fetching calendars from</Typography>
          <Typography color={colors.black600} sx={{
            fontWeight: 700
          }}>{getValues('email')}</Typography>
        </Box>
      ) : (
        <FormControl fullWidth>
          <FormLabel>Executive's email</FormLabel>
          <Controller
            name="email"
            control={emailControl}
            rules={{ required: true, pattern: emailRegex }}
            render={({ field: { ref, ...field } }) => (
              <CabTextInput {...field} />
            )}
          />
          {emailErrors.email && (
            <Alert severity="error" sx={{ marginTop: 1 }}>
              {emailErrors.email.message
              || (emailErrors?.email?.type === 'required' ? 'Email is required'
                : emailErrors.email?.type === 'pattern' ? 'Please enter a valid email address'
                  : null)}
            </Alert>
          )}
        </FormControl>
      )}
    </CabModal>
  );

  if (variant === 'inlineLink') {
    return (
      <>
        <InlineMissingCalendarPrompt>
          <CabIcon Icon={IoBulbOutline} alt="" sx={{ color: colors.black700, fontSize: 20, flexShrink: 0 }} />
          <InlineMissingCalendarText variant="body1">
            Still looking for a calendar?
          </InlineMissingCalendarText>
          <InlineMissingCalendarButton
            onClick={() => setShowEmailModal(true)}
            buttonType="text"
          >
            Click here to fetch it another way.
          </InlineMissingCalendarButton>
        </InlineMissingCalendarPrompt>
        {emailModal}
      </>
    );
  }

  return (
    <>
      <StyledBox>
        <ColoredBox sx={{ borderBottom: Object.keys(additionalCalendars).length ? '1px solid' : undefined }}>
          <Typography sx={{
            fontWeight: 700
          }}>Missing some of your team's calendars?</Typography>
          <Typography color={colors.black800}>
            If you have delegate access to a calendar, it may not show up in the list. It can be added by email below.
            <br /><br />Note: This will only find calendars for which you are a delegate
          </Typography>
          <Box>
            <CabButton
              onClick={() => setShowEmailModal(true)}
              buttonType="text"
              color="primary"
              sx={{marginLeft: -1, fontWeight: 700}}
            >
              Add executive email address
            </CabButton>
          </Box>
        </ColoredBox>
        {!loading ? (
          <>
            {additionalEmails.length > 0 && (
              <Box
                sx={{
                  width: '100%',
                  padding: 2
                }}>
                {additionalEmails.map((emailAddress, idx) => (
                  <Box key={emailAddress}>
                    {idx > 0 && (
                      <Divider />
                    )}
                    <Box
                      sx={{
                        paddingBottom: 1.5,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        display: 'flex',
                        gap: 1,
                        flexWrap: "wrap"
                      }}>
                      <Typography variant="body1" sx={{overflowWrap: 'anywhere'}}>
                        {emailAddress}
                      </Typography>
                      <CabToggleChip
                        label='Calendar Found'
                        icon={<CabIcon Icon={IoCheckmarkCircleOutline} sx={{ fontSize: 20, paddingBottom: '2px' }} />}
                        sx={{ 
                          backgroundColor: colors.lightBackgroundGreen, borderColor: colors.lightBackgroundGreen, 
                          height: 30, fontWeight: 500, color: colors.greenPrimary, 
                          '& .MuiChip-icon':{ color: colors.greenPrimary}
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              width: '100%',
              paddingLeft: 1,
              paddingRight: 1
            }}>
            <Skeleton height={50} width={'100%'} />
          </Box>
        )}
      </StyledBox>
      {emailModal}
    </>
  );
};

const InlineMissingCalendarPrompt = styled(Box, { label: "InlineMissingCalendarPrompt" })(() => ({
  alignItems: 'center',
  backgroundColor: colors.black50,
  borderRadius: 10,
  boxSizing: 'border-box',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  justifyContent: 'center',
  marginTop: 8,
  minHeight: 48,
  overflowX: 'visible',
  padding: '12px 16px',
  textAlign: 'center',
  width: '100%',
}));

const InlineMissingCalendarText = styled(Typography, { label: "InlineMissingCalendarText" })(() => ({
  color: colors.black700,
  flexShrink: 0,
  fontFamily: 'Inter',
  fontSize: 15,
  fontWeight: 400,
  lineHeight: '22px',
  whiteSpace: 'nowrap',
}));

const InlineMissingCalendarButton = styled(CabButton, { label: "InlineMissingCalendarButton" })(() => ({
  color: colors.black700,
  flexShrink: 0,
  fontFamily: 'Inter',
  fontSize: 15,
  fontWeight: 400,
  lineHeight: '22px',
  minWidth: 0,
  padding: 0,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: 'transparent',
    color: colors.black800,
    textDecoration: 'underline',
  },
}));

const StyledBox = styled(Box, {label: "StyledBox"})(() => ({
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: 500,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  border: '1px solid',
  borderColor: colors.black200,
  borderRadius: 6,
  marginRight: 8
}));

const ColoredBox = styled(Box, {label: "ColoredBox"})(() => ({
  maxWidth: 700,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  backgroundColor: colors.lightBackgroundGreen,
  padding: 16,
  borderRadius: 6,
  borderColor: colors.black200
}));
